import React, { useState, useEffect, useCallback } from 'react';
import { Nav, NavItem, NavLink, Button } from 'reactstrap';
import classnames from 'classnames';
import { appLanguages } from '../../common/languages';
import { langFlags } from '../../common/langFlags';
import i18n from '../../i18n';
import ContentTable from './ContentTable';
import NewContentForm from './NewContentForm';
import { getVoicesList } from '../../helpers/API/content/voices';

// Temporary import for migration functions
import '../../runMigration';

// Import fix script to make it available in browser console
import '../../scripts/runFixScript';

const ContentExtra = () => {
  const sections = ['podcasts', 'lectures', 'books'];
  const [activeSection, setActiveSection] = useState('podcasts');
  const [activeLanguage, setActiveLanguage] = useState(appLanguages[0] || 'en');
  const [addingContent, setAddingContent] = useState(false);
  const [voices, setVoices] = useState({});

  const toggleSection = useCallback((section) => {
    setActiveSection(section);
  }, []);

  const toggleLanguage = useCallback((lang) => {
    setActiveLanguage(lang);
  }, []);

  const toggleAddContent = useCallback(() => {
    setAddingContent((prev) => !prev);
  }, []);

  // Fetch voices for the active language if not already loaded
  useEffect(() => {
    const fetchVoices = async () => {
      if (!voices[activeLanguage]) {
        try {
          const data = await getVoicesList(activeLanguage);
          setVoices(prev => ({ ...prev, [activeLanguage]: data }));
        } catch (error) {
          console.error('Error fetching voices:', error);
        }
      }
    };
    fetchVoices();
  }, [activeLanguage, voices]);

  const activeVoices = voices[activeLanguage] || [];

  return (
    <div style={styles.container}>
      {/* Section Tabs */}
      <Nav tabs style={styles.tabs}>
        {sections.map((section) => (
          <NavItem key={section}>
            <NavLink
              onClick={() => toggleSection(section)}
              className={classnames({ active: activeSection === section })}
              style={
                activeSection === section
                  ? { ...styles.tabLink, ...styles.activeTabLink }
                  : styles.tabLink
              }
            >
              {i18n.t(section)}
            </NavLink>
          </NavItem>
        ))}
      </Nav>
      {/* Language Tabs */}
      <Nav tabs style={{ ...styles.tabs, marginTop: 10 }}>
        {appLanguages.map((lang) => (
          <NavItem key={lang}>
            <NavLink
              onClick={() => toggleLanguage(lang)}
              className={classnames({ active: activeLanguage === lang })}
              style={
                activeLanguage === lang
                  ? { ...styles.languageTabLink, ...styles.activeLanguageTabLink }
                  : styles.languageTabLink
              }
            >
              <img
                src={langFlags[lang]}
                alt={`${lang} flag`}
                style={styles.flag}
              />
              {lang.toUpperCase()}
            </NavLink>
          </NavItem>
        ))}
      </Nav>
      {/* Add Content Button */}
      <div style={styles.buttonContainer}>
        <Button color="primary" onClick={toggleAddContent}>
          {addingContent ? i18n.t('closeForm') : i18n.t('addContent')}
        </Button>
                <Button
          color="info"
          onClick={async () => {
            console.log('🔍 DEBUGGING: Testing chapter fetching...');
            try {
              const { getContentExtraWithChapters, getContentChapters } = await import('../../helpers/API/contentExtra');

              console.log(`📚 Testing ${activeSection}/${activeLanguage}`);
              const content = await getContentExtraWithChapters(activeSection, activeLanguage);
              console.log('📖 Content with chapters:', content);

              if (content.length > 0) {
                const firstItem = content[0];
                console.log(`🔍 First item (${firstItem.id}):`, firstItem);

                const directChapters = await getContentChapters(activeSection, activeLanguage, firstItem.id);
                console.log('📖 Direct chapter fetch:', directChapters);
              }
            } catch (error) {
              console.error('❌ Debug test failed:', error);
            }
          }}
          style={{ marginLeft: 10 }}
        >
          🔍 Debug Chapters
        </Button>
        <Button
          color="warning"
          onClick={async () => {
            console.log('🔄 MIGRATING: Running chapter migration...');
            try {
              const { migrateChaptersToSubcollection } = await import('../../helpers/API/contentExtra');

              // Get all content for this section/language
              const { getContentExtra } = await import('../../helpers/API/contentExtra');
              const content = await getContentExtra(activeSection, activeLanguage);

              console.log(`🚀 Found ${content.length} items to check for migration`);

              for (const item of content) {
                if (item.chapters && Array.isArray(item.chapters) && item.chapters.length > 0) {
                  console.log(`🔄 Migrating chapters for "${item.title}" (ID: ${item.id})`);
                  await migrateChaptersToSubcollection(activeSection, activeLanguage, item.id);
                  console.log(`✅ Migration completed for "${item.title}"`);
                }
              }

              console.log('🎉 Migration completed! Refreshing content...');
              // Refresh the content table
              window.location.reload();

            } catch (error) {
              console.error('❌ Migration failed:', error);
              alert('Migration failed. Check console for details.');
            }
          }}
          style={{ marginLeft: 10 }}
        >
          🔄 Migrate Chapters
        </Button>
      </div>
      {/* Either display the ContentTable or the NewContentForm */}
      {addingContent ? (
        <NewContentForm
          section={activeSection}
          language={activeLanguage}
          voices={activeVoices}
          onFinish={() => setAddingContent(false)}
        />
      ) : (
        <ContentTable
          section={activeSection}
          language={activeLanguage}
          voices={activeVoices}
        />
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: 20,
    marginTop: 120,
  },
  tabs: {
    marginBottom: 10,
  },
  tabLink: {
    cursor: 'pointer',
    padding: '10px 20px',
    marginRight: 10,
    backgroundColor: '#ddd',
    borderRadiusTopLeft: 8,
    borderRadiusTopRight: 8,
    color: '#333',
    fontWeight: '600',
    border: '1px solid #ddd',
  },
  activeTabLink: {
    backgroundColor: '#f7f7f7',
  },
  languageTabLink: {
    cursor: 'pointer',
    padding: '10px 20px',
    marginRight: 10,
    backgroundColor: '#f7f7f7',
    borderRadius: 8,
    color: '#333',
    fontWeight: '600',
    border: '1px solid #ddd',
  },
  activeLanguageTabLink: {
    backgroundColor: '#007bff',
    color: '#fff',
  },
  flag: {
    width: 20,
    height: 20,
    marginRight: 5,
  },
  buttonContainer: {
    marginBottom: 20,
  },
};

export default ContentExtra;
